// THE GUIDED DEAL (ux-guided-deal, owner feedback after the D192 walkthrough).
//
// Where a quote stands in the journey Build -> OEM prices -> Finalise ->
// Customer decision -> Order from the OEM -> Track the OEM order, worked out
// ONLY from what the quote screen already reads: the quote itself, the OEM
// confirmations view, the price requests, the outcome, the OEM orders and the
// gate's refusals. Nothing here is fetched, nothing is decided: every
// "blocked" reason is a message the screen or the server already gave.
//
// ⚠️ NOTHING HERE PRICES. Quantities and counts only; no money, ever.
// ⚠️ ABSENT IS NOT ZERO. A quantity the server did not report is not counted
// as 0 shipped; it is simply not counted.
//
// Built to grow: later steps (Receive goods, Deliver to the customer, Close
// out) are new entries in STEP_KEYS and new cases in deriveDealSteps; the bar
// and the card render whatever list they are given.

import type {
  GateRefusal, OemConfirmationsView, OemOrder, OemRequest,
  CustomsEntry, OrderStatusView,
} from "@/services/api";

// M4 (owner D195): 7 Receive goods, 8 Deliver to the customer, 9 Close out.
// A step keeps its NUMBER whatever is shown: a drop-ship-only deal goes
// 6 Track (delivered direct) -> 9 Close out, without 7 and 8.
export const STEP_KEYS = ["build", "oem", "finalise", "decision", "order", "track", "receive", "deliver", "close"] as const;
export type StepKey = typeof STEP_KEYS[number];

/** done / current (yours to do now) / waiting (on someone else) / todo / blocked. */
export type StepState = "done" | "current" | "waiting" | "todo" | "blocked";

export interface DealStep {
  key: StepKey;
  no: number;
  title: string;
  state: StepState;
  /** One line: what is true about this step now. */
  summary: string;
  /** Who the deal waits on, for state "waiting". */
  waitingOn?: string;
  /** The exact existing reasons, for state "blocked". */
  reasons?: string[];
}

export const STEP_TITLES: Record<StepKey, string> = {
  build: "Build the quote",
  oem: "Get OEM prices",
  finalise: "Finalise the quote",
  decision: "Customer decision",
  order: "Order from the OEM",
  track: "Track the OEM order",
  receive: "Receive goods",
  deliver: "Deliver to the customer",
  close: "Close out",
};
const STEP_NO: Record<StepKey, number> = {
  build: 1, oem: 2, finalise: 3, decision: 4, order: 5, track: 6, receive: 7, deliver: 8, close: 9,
};

export const STATE_LABEL: Record<StepState, string> = {
  done: "Done", current: "To do now", waiting: "Waiting", todo: "Not yet", blocked: "Blocked",
};

export type OutcomeKey = "open" | "won" | "lost" | "expired" | "superseded";

export interface DealInputs {
  /** The quote exists in the database (has an id). */
  saved: boolean;
  /** Finalised (has its number). */
  finalised: boolean;
  quoteName?: string | null;
  validUntil?: string | null;
  lineCount: number;
  unpricedCount: number;
  hasCustomer: boolean;
  /** Draft changes not yet saved. */
  unsaved: boolean;
  /** The approval block, as the server reported it. */
  approvalState?: "none" | "pending" | "approved" | "rejected";
  /** Finalising this draft is "Request approval" (GP below the limit, not an Admin). */
  finaliseIsApproval: boolean;
  /** The screen's own reasons why Finalise is off (unpriced, cannot price). */
  finaliseBlockedReasons: string[];
  canEdit: boolean;
  isAdmin: boolean;
  /** null = not loaded or not visible to this caller. */
  oem: OemConfirmationsView | null;
  requests: OemRequest[] | null;
  orders: OemOrder[] | null;
  outcome?: OutcomeKey | null;
  supersededBy?: string | null;
  /** The gate's current refusals per OEM (from the order form's own check). */
  gateRefusals: Record<string, GateRefusal[]>;
  /** Quantity still orderable on active confirmations, summed. */
  orderableQty: number;
  /** M4: what the fulfilment reads returned (null = not loaded / not visible). */
  fulfil?: { status: OrderStatusView | null; customs: CustomsEntry[]; grnCount: number; dnCount: number } | null;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const qtyText = (n: number) => String(Math.round(n * 1000) / 1000);

/** Every order that still counts (a cancelled order counts for nothing). */
export function liveOrders(orders: OemOrder[] | null): OemOrder[] {
  return (orders ?? []).filter((o) => o.status !== "cancelled");
}

/** Ordered / shipped / delivered across live orders. Unreported is not 0. */
export function orderTotals(orders: OemOrder[] | null) {
  let ordered = 0, shipped = 0, delivered = 0;
  for (const o of liveOrders(orders)) {
    for (const l of o.lines) {
      ordered += l.qty_ordered;
      if (l.shipped_qty != null) shipped += l.shipped_qty;
      if (l.delivered_qty != null) delivered += l.delivered_qty;
    }
  }
  return { ordered, shipped, delivered };
}

/** The quantity the quote needs from OEMs: every line the OEM view counts as quoted. */
export function quotedQty(oem: OemConfirmationsView | null): number {
  return (oem?.lines ?? []).filter((l) => l.status !== "not_quoted").reduce((s, l) => s + l.qty, 0);
}

export function deriveDealSteps(d: DealInputs): DealStep[] {
  const steps: DealStep[] = [];
  const push = (key: StepKey, state: StepState, summary: string, extra: Partial<DealStep> = {}) =>
    steps.push({ key, no: STEP_NO[key], title: STEP_TITLES[key], state, summary, ...extra });

  // 1 BUILD -------------------------------------------------------------------
  const buildProblems: string[] = [];
  if (d.lineCount === 0) buildProblems.push("No lines yet.");
  if (d.unpricedCount > 0) buildProblems.push(`${plural(d.unpricedCount, "line")} not priced yet.`);
  if (!d.hasCustomer) buildProblems.push("No customer chosen.");
  const buildDone = d.finalised || (d.saved && buildProblems.length === 0);
  push("build", buildDone ? "done" : "current",
    buildDone ? `${plural(d.lineCount, "line")}, all priced` : buildProblems.join(" ") || "Save the draft.");

  // 2 OEM PRICES ----------------------------------------------------------------
  const v = d.oem;
  const sentOpen = (d.requests ?? []).filter((r) => r.display_state === "sent");
  if (!d.saved || !v) {
    push("oem", "todo", d.saved ? "Not visible to you." : "Available after the draft is saved.");
  } else if (v.priced_lines === 0) {
    push("oem", buildDone ? "current" : "todo", "No line has a price for the OEM to confirm yet.");
  } else if (v.confirmed_lines >= v.priced_lines) {
    push("oem", "done", `${v.confirmed_lines} of ${plural(v.priced_lines, "line")} confirmed`);
  } else if (sentOpen.length > 0) {
    push("oem", "waiting", `${v.confirmed_lines} of ${plural(v.priced_lines, "line")} confirmed`,
      { waitingOn: sentOpen.map((r) => r.oem).join(", ") });
  } else {
    // After Won an OEM order needs them, so they are someone's move again.
    const due = d.finalised ? d.outcome === "won" : buildDone;
    push("oem", due ? "current" : "todo", `${v.confirmed_lines} of ${plural(v.priced_lines, "line")} confirmed`);
  }

  // 3 FINALISE ------------------------------------------------------------------
  if (d.finalised) {
    push("finalise", "done", `${d.quoteName ?? "Finalised"}${d.validUntil ? `, valid until ${d.validUntil}` : ""}`);
  } else if (d.approvalState === "pending") {
    push("finalise", "waiting", "Margin approval requested", { waitingOn: "an Admin" });
  } else if (d.finaliseBlockedReasons.length > 0) {
    push("finalise", "blocked", "Cannot be finalised yet", { reasons: d.finaliseBlockedReasons });
  } else {
    push("finalise", buildDone ? "current" : "todo",
      d.approvalState === "rejected" ? "Margin approval was rejected" : "Not finalised yet");
  }

  // 4 CUSTOMER DECISION ---------------------------------------------------------
  const outcome = d.outcome ?? null;
  if (!d.finalised) {
    push("decision", "todo", "Available after step 3");
  } else if (outcome === "won") {
    push("decision", "done", "Won");
  } else if (outcome === "lost") {
    push("decision", "done", "Lost: the deal is closed");
  } else if (outcome === "superseded") {
    push("decision", "done", `Replaced by ${d.supersededBy ?? "a later revision"}`);
  } else if (outcome === "expired") {
    push("decision", "waiting", "Past its valid-until date, no decision recorded", { waitingOn: "the customer" });
  } else {
    push("decision", "waiting", "Quote sent, no decision yet", { waitingOn: "the customer" });
  }

  // 5 ORDER FROM THE OEM -----------------------------------------------------------
  const closed = outcome === "lost" || outcome === "superseded";
  const totals = orderTotals(d.orders);
  const need = quotedQty(v);
  if (closed) {
    push("order", "todo", "Not needed: the quote is closed");
  } else if (outcome !== "won") {
    push("order", "todo", "Available after step 4");
  } else {
    const refusals = Object.values(d.gateRefusals).flat().map((r) => r.reason);
    const orderedText = need > 0 ? `${qtyText(totals.ordered)} of ${qtyText(need)} ordered` : `${qtyText(totals.ordered)} ordered`;
    if (need > 0 && totals.ordered >= need - 1e-9) {
      push("order", "done", orderedText);
    } else if (d.orderableQty <= 1e-9) {
      const reasons = ["Nothing confirmed is left to order."];
      if (v && v.awaiting_lines > 0) {
        reasons.push(`${v.awaiting_lines} of ${plural(v.priced_lines, "quoted line")} have no valid OEM confirmation.`);
      }
      push("order", "blocked", orderedText, { reasons });
    } else if (refusals.length > 0) {
      push("order", "blocked", orderedText, { reasons: refusals });
    } else {
      push("order", "current", orderedText);
    }
  }

  // 6 TRACK THE OEM ORDER ------------------------------------------------------------
  const live = liveOrders(d.orders);
  // M4: once an Admin has CLOSED the order out, steps 6-8 are finished as
  // they stand -- they say "Closed short" with what really happened, never
  // "waiting" for goods that will not come.
  const f = d.fulfil ?? null;
  const closedOut = !!f?.status?.closed;
  if (closed && live.length === 0) {
    push("track", "todo", "Not needed: the quote is closed");
  } else if (live.length === 0) {
    const cancelled = (d.orders ?? []).length;
    push("track", "todo", cancelled ? `${plural(cancelled, "order")} cancelled, none open` : "Available after step 5");
  } else {
    // A via-warehouse order's own journey ends when the OEM has SHIPPED it
    // (or its goods are already being received, step 7); a drop-ship order's
    // when it is DELIVERED direct to the customer.
    const all = live.every((o) => (o.ship_to_kind === "warehouse"
      ? ["shipped", "partially_delivered", "delivered"].includes(o.status) : o.status === "delivered"));
    const parts = [`${qtyText(totals.shipped)} of ${qtyText(totals.ordered)} shipped`,
      `${qtyText(totals.delivered)} delivered`];
    const late = live.filter((o) => o.late).length;
    const risk = live.filter((o) => o.at_risk).length;
    if (late) parts.push(`${late} late`);
    if (risk) parts.push(`${risk} at risk`);
    if (all) {
      const direct = live.every((o) => o.ship_to_kind !== "warehouse");
      push("track", "done", direct ? `${qtyText(totals.delivered)} of ${qtyText(totals.ordered)} delivered direct`
        // Goods received without a recorded "shipped" event were shipped all the same.
        : `${qtyText(Math.max(totals.shipped, totals.delivered))} of ${qtyText(totals.ordered)} shipped`);
    } else if (closedOut) {
      push("track", "done", `Closed short: ${qtyText(Math.max(totals.shipped, totals.delivered))} of ${qtyText(totals.ordered)} shipped`);
    } else {
      const ownMove = live.some((o) => o.status !== "placed");
      push("track", ownMove ? "current" : "waiting", parts.join(" · "),
        ownMove ? {} : { waitingOn: Array.from(new Set(live.map((o) => o.oem))).join(", ") });
    }
  }

  // 7 RECEIVE / 8 DELIVER / 9 CLOSE OUT (M4) ----------------------------------------------
  const via = live.filter((o) => o.ship_to_kind === "warehouse");
  const dropOnly = live.length > 0 && via.length === 0;
  const missingCustoms = customsMissing(live, f?.customs ?? []);
  if (!dropOnly) {
    if (closed || outcome !== "won" || via.length === 0) {
      push("receive", "todo", closed ? "Not needed: the quote is closed" : "Available after step 5 (orders to our warehouse)");
    } else {
      let need = 0, good = 0, dmg = 0;
      for (const o of via) for (const l of o.lines) {
        need += l.qty_ordered; good += l.received_qty ?? 0; dmg += l.damaged_qty ?? 0;
      }
      const sum = `${qtyText(good + dmg)} of ${qtyText(need)} received${dmg ? ` (${qtyText(dmg)} damaged)` : ""}`
        + (missingCustoms.length ? " · customs actuals not recorded" : "");
      if (good + dmg >= need - 1e-9 && !missingCustoms.length) push("receive", "done", sum);
      else if (closedOut) {
        push("receive", "done", (good + dmg >= need - 1e-9 ? sum
          : `Closed short: ${qtyText(good + dmg)} of ${qtyText(need)} received${dmg ? ` (${qtyText(dmg)} damaged)` : ""}`
            + (missingCustoms.length ? " · customs actuals not recorded" : "")));
      }
      else if (good + dmg >= need - 1e-9 || good + dmg > 1e-9
        || via.some((o) => ["shipped", "partially_delivered"].includes(o.status))) push("receive", "current", sum);
      else push("receive", "waiting", sum, { waitingOn: Array.from(new Set(via.map((o) => o.oem))).join(", ") });
    }
    const vlines = (f?.status?.lines ?? []).filter((l) => l.via_warehouse);
    if (closed || outcome !== "won" || via.length === 0 || !vlines.length) {
      push("deliver", "todo", closed ? "Not needed: the quote is closed" : "Available after step 7");
    } else {
      const q = vlines.reduce((s2, l) => s2 + l.qty, 0);
      const sent = vlines.reduce((s2, l) => s2 + (l.dispatched ?? 0), 0);
      const ready = vlines.reduce((s2, l) => s2 + (l.to_dispatch ?? 0), 0);
      const sum = `${qtyText(sent)} of ${qtyText(q)} dispatched${ready > 1e-9 ? ` · ${qtyText(ready)} ready to go` : ""}`;
      if (sent >= q - 1e-9) push("deliver", "done", sum);
      else if (closedOut) push("deliver", "done", `Closed short: ${qtyText(sent)} of ${qtyText(q)} delivered`);
      else if (ready > 1e-9) push("deliver", "current", sum);
      else push("deliver", "todo", sent > 1e-9 ? `${sum} · waiting for more goods` : "Available after goods are received (step 7)");
    }
  }
  if (closed || outcome !== "won") {
    push("close", "todo", closed ? "Not needed: the quote is closed" : "Available after step 4");
  } else if (closedOut) {
    push("close", "done", `Closed: ${f!.status!.closed!.reason}`);
  } else {
    const deliveredAll = live.length > 0 && steps.filter((x) => ["track", "receive", "deliver"].includes(x.key))
      .every((x) => x.state === "done");
    if (deliveredAll) {
      push("close", d.isAdmin ? "current" : "waiting", "Everything is delivered; ready to close",
        d.isAdmin ? {} : { waitingOn: "an Admin" });
    } else {
      push("close", "todo", "Available when everything is delivered");
    }
  }
  return steps;
}

/** Import (non-domestic) live orders with no live Bill of Entry recorded. */
export function customsMissing(live: OemOrder[], customs: CustomsEntry[]): OemOrder[] {
  return live.filter((o) => o.domestic === false
    && !customs.some((e) => e.oem_order_id === o.id && !e.reverses && !e.reversed));
}

/** The step the screen should focus: the first, in journey order, that is
 *  someone's move. Once the quote is finalised, OEM prices stop holding the
 *  focus: they matter again only for ordering, which step 5 checks. */
export function focusStep(steps: DealStep[]): DealStep | undefined {
  const finalised = steps.some((s) => s.key === "finalise" && s.state === "done");
  return steps.find((s) => !(finalised && s.key === "oem")
    && (s.state === "current" || s.state === "blocked" || s.state === "waiting"));
}

// ---- THE NEXT STEP CARD -------------------------------------------------------

export type NextActionKind =
  | "add-materials" | "update-prices" | "save-draft" | "request-prices" | "mark-sent"
  | "record-confirmation" | "finalise" | "request-approval" | "approve" | "mark-won"
  | "mark-lost" | "place-order" | "fix-order" | "record-event"
  | "receive-goods" | "record-customs" | "dispatch" | "close-out" | "none";

export interface NextAction {
  step?: StepKey;
  /** One sentence: what to do now (or who we are waiting on). */
  text: string;
  /** The primary action, or "none". */
  kind: NextActionKind;
  button?: string;
  /** What happens when you click. */
  consequence?: string;
  /** A secondary action while the move is someone else's. */
  secondary?: { kind: NextActionKind; button: string; consequence?: string };
  /** The reasons to list as a checklist. */
  checklist?: string[];
  /** For record-event: which order. */
  orderId?: number;
  /** For mark-sent: which request. */
  requestId?: number;
}

/** "Next: …" for one OEM order, from the server's derived status and flags. */
export function orderNextText(o: OemOrder): string {
  if (o.status === "cancelled") return "Cancelled. Nothing more to record.";
  if (o.status === "delivered") return "Delivered. Nothing more to record.";
  const risk = o.late ? " It is LATE." : o.at_risk ? " It is AT RISK." : "";
  const placed = o.placed_at ? o.placed_at.slice(0, 10) : null;
  if (o.status === "placed") {
    return `Next: waiting for ${o.oem} to acknowledge the order${placed ? ` (placed ${placed})` : ""}; `
      + `it is flagged at risk if no acknowledgement is recorded within 2 working days.${risk}`;
  }
  if (o.status === "acknowledged") return `Next: waiting for ${o.oem} to ship.${risk}`;
  if (o.status === "partially_shipped") return `Next: the rest to ship, and delivery of what shipped.${risk}`;
  if (o.status === "partially_delivered") return `Next: receive the rest into our warehouse (GRN).${risk}`;
  if (o.ship_to_kind === "warehouse") return `Next: receive the goods into our warehouse (GRN) when they arrive.${risk}`;
  return `Next: record the delivery when it arrives.${risk}`;
}

export function deriveNextAction(d: DealInputs, steps: DealStep[]): NextAction {
  const by = Object.fromEntries(steps.map((s) => [s.key, s])) as Record<StepKey, DealStep>;
  const v = d.oem;

  if (!d.canEdit && !d.isAdmin) {
    const f = focusStep(steps);
    return {
      step: f?.key, kind: "none",
      text: f ? `This deal is at step ${f.no}: ${f.title.toLowerCase()}. You can read it; the owner or an Admin acts on it.`
        : "Nothing is left to do on this deal.",
    };
  }

  // Step 1.
  if (d.lineCount === 0) {
    return { step: "build", kind: "add-materials", text: "Add the materials for this quote.", button: "Add Materials",
      consequence: "Opens the catalogue. Nothing is finalised; the draft saves as you work." };
  }
  if (d.unpricedCount > 0) {
    return { step: "build", kind: "none",
      text: `Give every line a price: ${plural(d.unpricedCount, "line")} has no net price yet. Type it in the table, or use an OEM quote price on the line.`,
    };
  }
  if (!d.finalised && d.approvalState === "pending") {
    if (d.isAdmin) {
      return { step: "finalise", kind: "approve", text: "This quote waits for a margin approval. You are an Admin: approve or reject it.",
        button: "Go to the approval", consequence: "Shows the approval panel. Approving lets the owner finalise." };
    }
    return { step: "finalise", kind: "none", text: "Waiting for an Admin to approve the margin. Nothing to do until then." };
  }
  if (!d.saved || d.unsaved) {
    return { step: "build", kind: "save-draft", text: "Save the draft so the OEM steps can use it.", button: "Save draft",
      consequence: "Saves the lines as a draft. No quote number is taken; nothing is sent." };
  }

  // Step 2 (before finalising): ask the OEM, record its answer.
  if (!d.finalised && v && v.priced_lines > 0 && v.confirmed_lines < v.priced_lines && by.oem) {
    const requests = d.requests ?? [];
    const draftReq = requests.find((r) => r.state === "draft");
    const sent = requests.filter((r) => r.display_state === "sent");
    const fin = finaliseAction(d);
    const finSecondary = fin.kind !== "none"
      ? { kind: fin.kind, button: `${fin.button} anyway`, consequence: fin.consequence } : undefined;
    if (draftReq) {
      return { step: "oem", kind: "mark-sent", requestId: draftReq.id,
        text: `Contact ${draftReq.oem} yourself (email or phone) for the final price and lead time, then mark the request as sent.`,
        button: `Mark sent to ${draftReq.oem}`,
        consequence: "Records that you sent it outside Anixify. Nothing is emailed.", secondary: finSecondary };
    }
    if (sent.length > 0) {
      return { step: "oem", kind: "record-confirmation",
        text: `Waiting for ${sent.map((r) => r.oem).join(", ")} to confirm. When the OEM replies, record its confirmation.`,
        button: "Record the OEM's confirmation",
        consequence: "Opens the confirmation form under step 2. A different price is shown, never applied to the quote.",
        secondary: finSecondary };
    }
    return { step: "oem", kind: "request-prices",
      text: "Ask the OEM to confirm the final price and lead time for each line.",
      button: "Request prices",
      consequence: "Creates one request per OEM in Anixify. Nothing is emailed: you contact the OEM yourself.",
      secondary: { kind: "record-confirmation", button: "I already have the OEM's confirmation",
        consequence: "Opens the confirmation form under step 2." } };
  }

  // Step 3.
  if (!d.finalised) return finaliseAction(d);

  // Step 4.
  const outcome = d.outcome ?? "open";
  if (outcome === "open" || outcome === "expired") {
    return { step: "decision", kind: "mark-won",
      text: outcome === "expired"
        ? "The quote is past its valid-until date and the customer has not decided. Record the decision when you have it."
        : "Waiting for the customer to decide. When they accept, mark the quote won.",
      button: "Mark won",
      consequence: "Records the customer's yes and opens OEM ordering. You may give the date we promised the customer.",
      secondary: { kind: "mark-lost", button: "Mark lost",
        consequence: "Asks for the reason. Closes the deal; nothing is ordered." } };
  }
  if (outcome === "lost") return { step: "decision", kind: "none", text: "This quote was lost. Nothing more to do." };
  if (outcome === "superseded") {
    return { step: "decision", kind: "none", text: `This quote was replaced by ${d.supersededBy ?? "a later revision"}. Work on that one.` };
  }

  // Step 5.
  if (by.order?.state === "blocked") {
    return { step: "order", kind: "fix-order", text: "The OEM order cannot be placed yet. What's missing:",
      checklist: by.order.reasons, button: "Go to the OEM order",
      consequence: "Shows the order form under step 5 with the same checklist." };
  }
  if (by.order?.state === "current") {
    return { step: "order", kind: "place-order",
      text: "Place the OEM order: choose the quantity and where the OEM ships to.",
      button: "Go to the OEM order",
      consequence: "Shows the order form. Placing takes the next OEM order number and cannot be undone. Nothing is emailed: print the order and send it yourself." };
  }

  // Steps 6-9.
  const live = liveOrders(d.orders);
  const closedOut = !!d.fulfil?.status?.closed;
  if (by.receive?.state === "current") {
    const o = live.find((x) => x.ship_to_kind === "warehouse" && x.status !== "delivered");
    if (o) {
      return { step: "receive", kind: "receive-goods", orderId: o.id,
        text: `When the goods for ${o.number} arrive at our warehouse, record what came: good units, and any short or damaged with a reason.`,
        button: "Record goods received",
        consequence: "Opens the receipt form. Each receipt (GRN) takes the next GRN number and is permanent; a mistake is put right by a reversing GRN." };
    }
    const miss = customsMissing(live, d.fulfil?.customs ?? []);
    if (miss.length) {
      return { step: "receive", kind: "record-customs", orderId: miss[0].id,
        text: `Record the customs actuals for ${miss[0].number} from its Bill of Entry: duty, charges and what the bank paid.`,
        button: "Record customs actuals",
        consequence: "Opens the Bill of Entry form. Empty boxes stay \"not recorded\". Used only by the Admin's Actual margin report -- never by pricing." };
    }
  }
  if (by.deliver?.state === "current") {
    return { step: "deliver", kind: "dispatch",
      text: "Goods are in our warehouse: send them to the customer with a delivery note (challan).",
      button: "Create a delivery note",
      consequence: "Opens the delivery form. The challan takes the next delivery note number and shows no price and no OEM. Nothing is emailed: print it." };
  }
  // A closed order asks for nothing more (D196 fix-up), whatever is still open on it.
  if (closedOut) return { step: "close", kind: "none", text: "The order is closed. Nothing more to do." };
  const open = live.filter((o) => (o.ship_to_kind === "warehouse"
    ? !["shipped", "partially_delivered", "delivered"].includes(o.status) : o.status !== "delivered"));
  if (open.length > 0) {
    const o = open.find((x) => x.late) ?? open.find((x) => x.at_risk) ?? open[0];
    const waitingOem = o.status === "placed" || o.status === "acknowledged";
    return { step: "track", kind: "record-event", orderId: o.id,
      text: `${o.number}: ${orderNextText(o)}`,
      button: waitingOem ? `Record what ${o.oem} told you` : "Record shipment or delivery",
      consequence: "Opens the event form for this order. Each entry is permanent; a mistake is put right by a later entry." };
  }
  if (by.receive?.state === "waiting") {
    return { step: "receive", kind: "none", text: `Waiting for ${by.receive.waitingOn ?? "the OEM"} to deliver to our warehouse. Record the goods when they arrive.` };
  }
  if (by.close?.state === "current" && d.isAdmin) {
    return { step: "close", kind: "close-out", text: "Everything is delivered. Close the order to finish the deal.",
      button: "Close the order",
      consequence: "Asks for a reason and records the close. No figure, quote or number changes; an Admin can reopen it." };
  }
  if (by.close?.state === "waiting") {
    return { step: "close", kind: "none", text: "Everything is delivered. Waiting for an Admin to close the order." };
  }
  if (closedOut) return { step: "close", kind: "none", text: "The order is closed. Nothing more to do." };
  if (live.length > 0) {
    return { step: "track", kind: "none", text: "Every OEM order on this quote is delivered. Nothing more to do here." };
  }
  return { kind: "none", text: "Nothing is left to do on this deal." };
}

function finaliseAction(d: DealInputs): NextAction {
  if (d.finaliseBlockedReasons.length > 0) {
    return { step: "finalise", kind: "none", text: "The quote cannot be finalised yet:", checklist: d.finaliseBlockedReasons };
  }
  if (!d.hasCustomer) {
    return { step: "build", kind: "none", text: "Choose the customer for this quote before finalising it." };
  }
  if (d.finaliseIsApproval) {
    return { step: "finalise", kind: "request-approval",
      text: "The margin is below the approval limit: ask an Admin to approve it.",
      button: "Request approval",
      consequence: "Saves the draft and locks it for an Admin to approve. No quote number is taken yet." };
  }
  return { step: "finalise", kind: "finalise", text: "Finalise the quote so it can go to the customer.",
    button: "Finalise quote",
    consequence: "Finalising takes the next quote number and locks the prices. It cannot be undone." };
}
